// @ts-nocheck - Legacy file, will be refactored

import {
	Button,
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
	Input,
	Label,
	Slider,
	Switch,
	Textarea,
} from "@ancatag/at-design";
import { Plus, X } from "lucide-react";
import type { WizardData } from "../NewModelConfig";

interface Step5ConfigurationProps {
	wizardData: WizardData;
	onUpdate: (updates: Partial<WizardData>) => void;
}

export function Step5Configuration({
	wizardData,
	onUpdate,
}: Step5ConfigurationProps) {
	// Check if provider is Ollama (Ollama-specific parameters)
	const isOllama = wizardData.provider?.identifier === "ollama";

	const addPrePromptItem = () => {
		onUpdate({
			prePromptItems: [...wizardData.prePromptItems, ""],
		});
	};

	const updatePrePromptItem = (index: number, value: string) => {
		const updated = [...wizardData.prePromptItems];
		updated[index] = value;
		onUpdate({ prePromptItems: updated });
	};

	const removePrePromptItem = (index: number) => {
		const updated = wizardData.prePromptItems.filter(
			(_: string, i: number) => i !== index,
		);
		onUpdate({ prePromptItems: updated });
	};

	return (
		<div className="space-y-6">
			<div>
				<p className="text-sm text-muted-foreground mb-4">
					Configure generation parameters, caching, and RAG settings for your
					model configuration.
				</p>
			</div>

			{/* Basic Settings */}
			<Card>
				<CardHeader>
					<CardTitle>Basic Settings</CardTitle>
					<CardDescription>
						Name and describe your model configuration
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="configName">
							Configuration Name <span className="text-destructive">*</span>
						</Label>
						<Input
							id="configName"
							placeholder="My Ollama Config"
							value={wizardData.configName}
							onChange={(e) => onUpdate({ configName: e.target.value })}
							required
						/>
					</div>
					<div className="space-y-2">
						<Label htmlFor="description">Description</Label>
						<Textarea
							id="description"
							placeholder="Optional description for this configuration"
							value={wizardData.description}
							onChange={(e) => onUpdate({ description: e.target.value })}
							rows={3}
						/>
					</div>
				</CardContent>
			</Card>

			{/* Generation Parameters */}
			<Card>
				<CardHeader>
					<CardTitle>Generation Parameters</CardTitle>
					<CardDescription>
						Configure how the model generates responses
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<Label htmlFor="temperature">
								Temperature: {wizardData.parameters.temperature ?? 0.7}
							</Label>
							<span className="text-xs text-muted-foreground">0.0 - 2.0</span>
						</div>
						<Slider
							id="temperature"
							min={0}
							max={2}
							step={0.1}
							value={[wizardData.parameters.temperature ?? 0.7]}
							onValueChange={([value]) =>
								onUpdate({
									parameters: { ...wizardData.parameters, temperature: value },
								})
							}
						/>
						<p className="text-xs text-muted-foreground">
							Controls randomness. Lower = more focused, Higher = more creative
						</p>
					</div>

					<div className="space-y-2">
						<Label htmlFor="maxTokens">Max Tokens</Label>
						<Input
							id="maxTokens"
							type="number"
							min={1}
							max={100000}
							value={wizardData.parameters.maxTokens || ""}
							onChange={(e) =>
								onUpdate({
									parameters: {
										...wizardData.parameters,
										maxTokens: e.target.value
											? parseInt(e.target.value, 10)
											: undefined,
									},
								})
							}
						/>
						<p className="text-xs text-muted-foreground">
							Maximum number of tokens to generate (leave empty for model
							default)
						</p>
					</div>

					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<Label htmlFor="topP">
								Top P: {wizardData.parameters.topP ?? 0.95}
							</Label>
							<span className="text-xs text-muted-foreground">0.0 - 1.0</span>
						</div>
						<Slider
							id="topP"
							min={0}
							max={1}
							step={0.01}
							value={[wizardData.parameters.topP ?? 0.95]}
							onValueChange={([value]) =>
								onUpdate({
									parameters: { ...wizardData.parameters, topP: value },
								})
							}
						/>
						<p className="text-xs text-muted-foreground">
							Nucleus sampling: Controls diversity of vocabulary (higher = more
							diverse)
						</p>
					</div>

					{/* Ollama-specific parameters */}
					{isOllama && (
						<>
							<div className="space-y-2">
								<Label htmlFor="topK">Top K</Label>
								<Input
									id="topK"
									type="number"
									min={1}
									max={100}
									value={wizardData.parameters.topK || ""}
									onChange={(e) =>
										onUpdate({
											parameters: {
												...wizardData.parameters,
												topK: e.target.value
													? parseInt(e.target.value, 10)
													: undefined,
											},
										})
									}
								/>
								<p className="text-xs text-muted-foreground">
									Limits vocabulary to top K most likely tokens (1-100, leave
									empty for default)
								</p>
							</div>

							<div className="space-y-2">
								<div className="flex items-center justify-between">
									<Label htmlFor="repeatPenalty">
										Repeat Penalty: {wizardData.parameters.repeatPenalty ?? 1.1}
									</Label>
									<span className="text-xs text-muted-foreground">
										1.0 - 2.0
									</span>
								</div>
								<Slider
									id="repeatPenalty"
									min={1.0}
									max={2.0}
									step={0.05}
									value={[wizardData.parameters.repeatPenalty ?? 1.1]}
									onValueChange={([value]) =>
										onUpdate({
											parameters: {
												...wizardData.parameters,
												repeatPenalty: value,
											},
										})
									}
								/>
								<p className="text-xs text-muted-foreground">
									Reduces repetitive phrases (higher = less repetition)
								</p>
							</div>

							<div className="space-y-2">
								<Label htmlFor="contextWindow">Context Window</Label>
								<Input
									id="contextWindow"
									type="number"
									min={1024}
									max={32768}
									step={1024}
									value={wizardData.parameters.contextWindow || ""}
									onChange={(e) =>
										onUpdate({
											parameters: {
												...wizardData.parameters,
												contextWindow: e.target.value
													? parseInt(e.target.value, 10)
													: undefined,
											},
										})
									}
								/>
								<p className="text-xs text-muted-foreground">
									Maximum tokens for conversation history (1024-32768, leave
									empty for default 8192)
								</p>
							</div>

							<div className="space-y-2">
								<div className="flex items-center justify-between">
									<Label>Stop Sequences</Label>
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => {
											const current = wizardData.parameters.stopSequences || [];
											onUpdate({
												parameters: {
													...wizardData.parameters,
													stopSequences: [...current, ""],
												},
											});
										}}
									>
										<Plus className="h-4 w-4 mr-1" />
										Add Sequence
									</Button>
								</div>
								<p className="text-xs text-muted-foreground mb-2">
									Tokens that stop generation when encountered (e.g.,
									"&lt;|im_end|&gt;")
								</p>
								{!wizardData.parameters.stopSequences ||
								wizardData.parameters.stopSequences.length === 0 ? (
									<div className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded-lg">
										No stop sequences. Click "Add Sequence" to add one.
									</div>
								) : (
									<div className="space-y-2">
										{wizardData.parameters.stopSequences.map(
											(seq: string, index: number) => (
												<div key={index} className="flex gap-2">
													<Input
														placeholder="Stop sequence (e.g., &lt;|im_end|&gt;)"
														value={seq}
														onChange={(e) => {
															const updated = [
																...(wizardData.parameters.stopSequences || []),
															];
															updated[index] = e.target.value;
															onUpdate({
																parameters: {
																	...wizardData.parameters,
																	stopSequences: updated,
																},
															});
														}}
													/>
													<Button
														type="button"
														variant="ghost"
														size="icon"
														className="h-10 w-10"
														onClick={() => {
															const updated = (
																wizardData.parameters.stopSequences || []
															).filter((_: string, i: number) => i !== index);
															onUpdate({
																parameters: {
																	...wizardData.parameters,
																	stopSequences:
																		updated.length > 0 ? updated : undefined,
																},
															});
														}}
														aria-label="Remove stop sequence"
													>
														<X className="h-5 w-5" />
													</Button>
												</div>
											),
										)}
									</div>
								)}
							</div>
						</>
					)}
				</CardContent>
			</Card>

			{/* Prompt Format */}
			<Card>
				<CardHeader>
					<CardTitle>Prompt Format</CardTitle>
					<CardDescription>System prompt and pre-prompt items</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="systemPrompt">System Prompt</Label>
						<Textarea
							id="systemPrompt"
							placeholder="You are a helpful assistant..."
							value={wizardData.systemPrompt}
							onChange={(e) => onUpdate({ systemPrompt: e.target.value })}
							rows={4}
						/>
						<p className="text-xs text-muted-foreground">
							System prompt that defines the model's behavior and role
						</p>
					</div>

					<div className="space-y-2">
						<div className="flex items-center justify-between">
							<Label>Pre-prompt Items</Label>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={addPrePromptItem}
							>
								<Plus className="h-4 w-4 mr-1" />
								Add Item
							</Button>
						</div>
						<p className="text-xs text-muted-foreground mb-2">
							Text that will be prepended to each user message (useful for
							context or instructions)
						</p>
						{wizardData.prePromptItems.length === 0 ? (
							<div className="text-sm text-muted-foreground py-4 text-center border border-dashed rounded-lg">
								No pre-prompt items. Click "Add Item" to add one.
							</div>
						) : (
							<div className="space-y-2">
								{wizardData.prePromptItems.map(
									(item: string, index: number) => (
										<div key={index} className="flex gap-2">
											<Textarea
												placeholder="Pre-prompt item..."
												value={item}
												onChange={(e) =>
													updatePrePromptItem(index, e.target.value)
												}
												rows={2}
												className="flex-1"
											/>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className="h-10 w-10"
												onClick={() => removePrePromptItem(index)}
												aria-label="Remove pre-prompt item"
											>
												<X className="h-5 w-5" />
											</Button>
										</div>
									),
								)}
							</div>
						)}
					</div>
				</CardContent>
			</Card>

			{/* Response Format */}
			<Card>
				<CardHeader>
					<CardTitle>Response Format</CardTitle>
					<CardDescription>
						Configure structured JSON output format for classification, data
						extraction, and API integration
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label>Format Type</Label>
						<select
							className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
							value={wizardData.responseFormat?.type || "none"}
							onChange={(e) => {
								if (e.target.value === "none") {
									onUpdate({ responseFormat: undefined });
								} else {
									onUpdate({
										responseFormat: {
											type: e.target.value,
											...(e.target.value === "classification" && {
												categories: [],
												returnConfidence: true,
												returnReason: false,
											}),
											...(e.target.value === "structured" && {
												schema: {
													type: "object",
													properties: {},
													required: [],
												},
											}),
											...(e.target.value === "custom" && {
												format: "",
											}),
										},
									});
								}
							}}
						>
							<option value="none">None (Default)</option>
							<option value="json_object">JSON Object</option>
							<option value="classification">Classification</option>
							<option value="structured">Structured Schema</option>
							<option value="custom">Custom Format</option>
						</select>
						<p className="text-xs text-muted-foreground">
							Select a format type to enforce structured responses
						</p>
					</div>

					{wizardData.responseFormat?.type === "classification" && (
						<div className="space-y-4 p-4 border rounded-lg bg-muted/50">
							<div className="space-y-2">
								<Label>Categories (one per line)</Label>
								<Textarea
									placeholder="nsfw&#10;spam&#10;toxicity"
									value={(wizardData.responseFormat?.categories || []).join(
										"\n",
									)}
									onChange={(e) => {
										const categories = e.target.value
											.split("\n")
											.map((c) => c.trim())
											.filter((c) => c.length > 0);
										onUpdate({
											responseFormat: {
												...wizardData.responseFormat,
												categories,
											},
										});
									}}
									rows={4}
								/>
								<p className="text-xs text-muted-foreground">
									Enter category names, one per line
								</p>
							</div>
							<div className="flex items-center space-x-2">
								<input
									type="checkbox"
									id="returnConfidence"
									checked={
										wizardData.responseFormat?.returnConfidence !== false
									}
									onChange={(e) => {
										onUpdate({
											responseFormat: {
												...wizardData.responseFormat,
												returnConfidence: e.target.checked,
											},
										});
									}}
									className="rounded border-gray-300"
								/>
								<Label htmlFor="returnConfidence" className="cursor-pointer">
									Return confidence scores
								</Label>
							</div>
							<div className="flex items-center space-x-2">
								<input
									type="checkbox"
									id="returnReason"
									checked={wizardData.responseFormat?.returnReason || false}
									onChange={(e) => {
										onUpdate({
											responseFormat: {
												...wizardData.responseFormat,
												returnReason: e.target.checked,
											},
										});
									}}
									className="rounded border-gray-300"
								/>
								<Label htmlFor="returnReason" className="cursor-pointer">
									Return reason/explanation
								</Label>
							</div>
						</div>
					)}

					{wizardData.responseFormat?.type === "structured" && (
						<div className="space-y-2 p-4 border rounded-lg bg-muted/50">
							<Label>JSON Schema</Label>
							<Textarea
								placeholder='{"type": "object", "properties": {...}, "required": [...]}'
								value={JSON.stringify(
									wizardData.responseFormat?.schema || {},
									null,
									2,
								)}
								onChange={(e) => {
									try {
										const schema = JSON.parse(e.target.value);
										onUpdate({
											responseFormat: {
												...wizardData.responseFormat,
												schema,
											},
										});
									} catch {
										// Invalid JSON, ignore
									}
								}}
								rows={8}
								className="font-mono text-sm"
							/>
							<p className="text-xs text-muted-foreground">
								Enter a valid JSON Schema to enforce response structure
							</p>
						</div>
					)}

					{wizardData.responseFormat?.type === "custom" && (
						<div className="space-y-2 p-4 border rounded-lg bg-muted/50">
							<Label>Custom Format</Label>
							<Input
								placeholder="csv, xml, etc."
								value={wizardData.responseFormat?.format || ""}
								onChange={(e) => {
									onUpdate({
										responseFormat: {
											...wizardData.responseFormat,
											format: e.target.value,
										},
									});
								}}
							/>
							<p className="text-xs text-muted-foreground">
								Specify the custom format type
							</p>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Cache Settings */}
			<Card>
				<CardHeader>
					<CardTitle>Cache Settings</CardTitle>
					<CardDescription>
						Configure caching to save tokens and improve response times
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="flex items-center justify-between">
						<div className="space-y-0.5">
							<Label htmlFor="hotCache">Hot Cache (Exact Match)</Label>
							<p className="text-xs text-muted-foreground">
								Cache responses for identical prompts (instant responses)
							</p>
						</div>
						<Switch
							id="hotCache"
							checked={wizardData.hotCacheEnabled}
							onCheckedChange={(checked) =>
								onUpdate({ hotCacheEnabled: checked })
							}
						/>
					</div>

					<div className="flex items-center justify-between">
						<div className="space-y-0.5">
							<Label htmlFor="semanticCache">
								Semantic Cache (95% Similarity)
							</Label>
							<p className="text-xs text-muted-foreground">
								Cache responses for semantically similar prompts (plan-based)
							</p>
						</div>
						<Switch
							id="semanticCache"
							checked={wizardData.semanticCacheEnabled}
							onCheckedChange={(checked) =>
								onUpdate({ semanticCacheEnabled: checked })
							}
						/>
					</div>
				</CardContent>
			</Card>

			{/* RAG Settings */}
			<Card>
				<CardHeader>
					<CardTitle>RAG (Retrieval Augmented Generation)</CardTitle>
					<CardDescription>
						Enable RAG to reduce context tokens with smart document chunking
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="flex items-center justify-between">
						<div className="space-y-0.5">
							<Label htmlFor="ragEnabled">Enable RAG</Label>
							<p className="text-xs text-muted-foreground">
								Use RAG to retrieve relevant document chunks instead of full
								context
							</p>
						</div>
						<Switch
							id="ragEnabled"
							checked={wizardData.ragEnabled}
							onCheckedChange={(checked) => onUpdate({ ragEnabled: checked })}
						/>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}
